import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Line, Pie } from 'react-chartjs-2';
import '../styles/StatisticsTab.css';
import { API_BASE_URL } from '../config';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement, 
  ArcElement,    
  Title,
  Tooltip,
  Legend,
  LineElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,  
  ArcElement,     
  Title,
  Tooltip,
  Legend,
  LineElement,
);

const StatisticsTab = ({ id }) => {
  const [taskData, setTaskData] = useState([]);
  const [averageTime, setAverageTime] = useState('00:00:00');
  const [tasksOverTime, setTasksOverTime] = useState({});
  const [deadlineStats, setDeadlineStats] = useState([]);
  const [taskSummary, setTaskSummary] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(5);

  useEffect(() => {
    let savedUser = JSON.parse(localStorage.getItem('user'));
    axios.get(`${API_BASE_URL}/api/v1/task/${id}`,{headers:{"Authorization":`Bearer ${savedUser["jwtToken"]}`}})
      .then(response => setTaskData(response.data))
      .catch(error => console.error('Error fetching tasks:', error));
  }, [id]);

  useEffect(() => {
    if (taskData.length > 0) {
      const validTasks = taskData.filter(task => task.start && task.end);

      const times = validTasks.map(task => {
        const start = moment(task.start);
        const end = moment(task.end);
        return end.diff(start, 'seconds');
      });

      const averageSeconds = times.reduce((a, b) => a + b, 0) / times.length;
      const averageTimeFormatted = averageSeconds ? moment.utc(averageSeconds * 1000).format('HH:mm:ss') : '00:00:00';

      setAverageTime(averageTimeFormatted);
    }
  }, [taskData]);

  useEffect(() => {
    let savedUser = JSON.parse(localStorage.getItem('user'));
    axios.get(`${API_BASE_URL}/api/v1/task/tasks-over-time/${id}`,{headers:{"Authorization":`Bearer ${savedUser["jwtToken"]}`}})
      .then(response => setTasksOverTime(response.data))
      .catch(error => console.error('Error fetching tasks over time:', error));
  }, [id]);

  useEffect(() => {
    let savedUser = JSON.parse(localStorage.getItem('user'));
    axios.get(`${API_BASE_URL}/api/v1/task/deadline-stats/${id}`,{headers:{"Authorization":`Bearer ${savedUser["jwtToken"]}`}})
      .then(response => setDeadlineStats(response.data))
      .catch(error => console.error('Error fetching deadline stats:', error));
  }, [id]);

  useEffect(() => {
    let savedUser = JSON.parse(localStorage.getItem('user'));
    axios.get(`${API_BASE_URL}/api/v1/task/task-summary/${id}`,{headers:{"Authorization":`Bearer ${savedUser["jwtToken"]}`}})
      .then(response => setTaskSummary(response.data))
      .catch(error => console.error('Error fetching task summary:', error));
  }, [id]);

  const sortedTasksOverTime = Object.entries(tasksOverTime)
    .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB));

  const lineGraphLabels = sortedTasksOverTime.map(([date]) => date);
  const lineGraphData = sortedTasksOverTime.map(([, value]) => value);

  const lineGraphChartData = {
    labels: lineGraphLabels,
    datasets: [
      {
        label: 'Završeni zadaci',
        data: lineGraphData,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const pieChartData = {
    labels: ['Na vrijeme', 'Kašnjenje'],
    datasets: [
      {
        label: 'Zadaci',
        data: deadlineStats,
        backgroundColor: ['#4caf50', '#f44336'],
        borderWidth: 1,
      },
    ],
  };

  const lineGraphOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
    scales: {
      y: {
        min: 0, 
        max: 10, 
        ticks: {
          stepSize: 2,  
          callback: function(value) {
            return value % 2 === 0 ? value : '';  
          },
        },
      },
    },
  };

  const handleRowClick = (task) => {
    setSelectedTask(task); 
    
  };

  const handlePagination = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = taskSummary.slice(indexOfFirstTask, indexOfLastTask);

  const totalPages = Math.ceil(taskSummary.length / tasksPerPage);

  const secondsToHM = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours < 10 ? '0' + hours : hours}:${remainingMinutes < 10 ? '0' + remainingMinutes : remainingMinutes}`;
  };

  const lineGraphTwoOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
    scales: {
      y: {
        type: 'linear',
        min: 0,
        ticks: {
          stepSize: 3600, 
          callback: function(value) {
            return secondsToHM(value); 
          },
        },
      },
    },
  };

  return (
    <div className="statistics-container">
      <div className="statistics-average-container average">
        <div className="statistics-average-title">{averageTime || '00:00:00'}</div>
        <div className="statistics-average-value">Prosječno vrijeme potrebno za izradu zadatka</div>
      </div>

      <div className="line-graph-container">
        <h3>Završeni zadaci za tekući mjesec</h3>
        <Line data={lineGraphChartData} options={lineGraphOptions}  className="line-chart" />
      </div>

      <div className="pie-chart-container">
        <h3>Završeni zadaci u odnosu na rokove</h3>
        <Pie data={pieChartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} className="pie-chart" />
      </div>

      <div className="task-summary-table">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Naziv zadatka</th>
          </tr>
        </thead>
        <tbody>
          {currentTasks.map((task, index) => (
            <tr key={task.taskName} onClick={() => handleRowClick(task)}>
              <td>{index + 1}</td>
              <td>{task.taskName}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pagination">
        <button 
          onClick={() => handlePagination(currentPage - 1)} 
          disabled={currentPage === 1} 
          className="pagination-button"
        >
          &#8249; 
        </button>

        <span className="pagination-info">Stranica {currentPage} od {totalPages}</span>

        <button 
          onClick={() => handlePagination(currentPage + 1)} 
          disabled={currentPage === totalPages} 
          className="pagination-button"
        >
          &#8250; 
          </button>
        </div>
      </div>

      {selectedTask && (
        <div className="task-line-graph-container">
          <Line
            data={{
              labels: [...selectedTask.taskDetails]
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map(item => item.date),
              datasets: [{
                label: selectedTask.taskName,
                data: [...selectedTask.taskDetails]
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map(item => item.executionTime),
                fill: false,
                borderColor: '#72b4d1',
                tension: 0.1,
              }],
            }}
            options={lineGraphTwoOptions}
            className="line-chart-selected-task"
          />
        </div>
      )}
    </div>
  );
};

export default StatisticsTab;
